import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  BuildingStorefrontIcon,
  EnvelopeIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import { AuthAPI } from "../../api/auth";
import { TokenManager } from "../../api/config";
import { auth, setupRecaptcha } from "../../lib/firebase";
import { signInWithPhoneNumber, ConfirmationResult } from "firebase/auth";

interface Step1Data {
  recipient: string;
}

interface Step2Data {
  otp: string;
}

interface Step3Data {
  full_name: string;
  shop_name: string;
  pin: string;
}

const FrictionlessRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [recipient, setRecipient] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [verifier, setVerifier] = useState<any>(null);

  const otpInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Form Hooks for each step
  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    watch: watchStep1,
    formState: { errors: errorsStep1 },
  } = useForm<Step1Data>();

  const recipientValue = watchStep1("recipient");

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
  } = useForm<Step2Data>();

  const {
    register: registerStep3,
    handleSubmit: handleSubmitStep3,
    formState: { errors: errorsStep3 },
  } = useForm<Step3Data>();

  // Use refs from register for focus management
  const { ref: otpRef, ...otpRegister } = registerStep2("otp", { 
    required: "OTP is required", 
    minLength: { value: 6, message: "Enter 6-digit code" } 
  });
  
  const { ref: nameRef, ...nameRegister } = registerStep3("full_name", { 
    required: "Name is required" 
  });

  // Initialize reCAPTCHA on mount
  useEffect(() => {
    // Use a small timeout to ensure the element is in the DOM
    const timer = setTimeout(() => {
      try {
        const v = setupRecaptcha("registration-recaptcha");
        setVerifier(v);
      } catch (err) {
        console.error("Firebase Recaptcha init error:", err);
      }
    }, 500);
    
    // Cleanup on unmount
    return () => {
       clearTimeout(timer);
       if (verifier) {
          try {
             verifier.clear();
          } catch (e) {
            console.warn('Recaptcha cleanup failed', e);
          }
       }
    };
  }, []);

  // ... (auto-focus effect and other state)

  // STEP 1: Request OTP (also checks for duplicate accounts)
  const onRequestOtp = async (data: Step1Data) => {
    setIsLoading(true);
    setError(null);
    try {
      // Request OTP via backend — backend also checks for duplicate accounts
      const response = await AuthAPI.requestOtp(data.recipient, 'register') as any;

      // If backend returned an error object (CORS-masked or otherwise), show it
      if (response && (response.error || response.status_code >= 400)) {
        const msg = response.message || response.detail || "This mobile number is already registered. Please login.";
        setError(msg);
        return; // Do NOT advance to next step
      }

      setRecipient(data.recipient);

      if (response && response.otp_disabled) {
        // OTP is disabled — duplicate check already passed on backend, proceed to profile
        setStep(3);
      } else {
        // OTP sent — proceed to verification step
        setStep(2);
      }
    } catch (err: any) {
      console.error("Step 1 Error:", err);
      // Extract message from either 'message', 'detail', or fallback
      const msg = err?.message || err?.detail || err?.error || "Failed to send verification code. Please try again.";
      setError(msg);
      // Do NOT advance — stay on step 1
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Verify OTP
  const onVerifyOtp = async (data: Step2Data) => {
    setIsLoading(true);
    setError(null);
    try {
      // Verify both email and phone via Backend (returns token)
      const response = await AuthAPI.verifyOtp({
        recipient,
        otp: data.otp,
        purpose: 'register'
      });
      
      TokenManager.setToken(response.access_token);
      if (response.refresh_token) {
        TokenManager.setRefreshToken(response.refresh_token);
      }
      
      setStep(3);
    } catch (err: any) {
      setError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Complete Profile
  const onCompleteProfile = async (data: Step3Data) => {
    setIsLoading(true);
    setError(null);
    try {
      const isPhone = !recipient.includes("@");

      if (!TokenManager.isAuthenticated()) {
        // Signup with PIN
        const response = await AuthAPI.signupWithPin({
          recipient: recipient,
          otp: isPhone ? "FIREBASE" : "DISABLED", 
          pin: data.pin,
          full_name: data.full_name,
          shop_name: data.shop_name,
          is_firebase_verified: true
        });

        TokenManager.setToken(response.access_token);
        if (response.refresh_token) {
          TokenManager.setRefreshToken(response.refresh_token);
        }
      } else {
        // Logged in (Step 2 backend verify logged them in)
        await AuthAPI.updateProfile({
          full_name: data.full_name,
          shop_name: data.shop_name,
          pin: data.pin,
        });
      }

      navigate("/registration-success");

    } catch (err: any) {
      setError(err.message || "Failed to finalize registration.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8">
      {/* Hidden Recaptcha */}
      <div id="registration-recaptcha"></div>

      <div className="max-w-md w-full space-y-6 bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 border border-slate-100 p-1">
            <img src="/logo-icon.svg" alt="Prindox Logo" className="w-12 h-12 object-contain" />
          </div>
          <h2 className="mt-6 text-3xl font-black text-gray-900 tracking-tight">
            Launch Your Shop
          </h2>
          
          {/* Progress Bar */}
          <div className="mt-6 flex items-center justify-center space-x-3">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`h-2 rounded-full transition-all duration-500 ${
                  s === step ? 'w-10 bg-blue-600' : s < step ? 'w-4 bg-green-500' : 'w-4 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <p className="mt-4 text-sm font-medium text-gray-500">
            {step === 1 && "Start with your mobile or email."}
            {step === 2 && "Enter the verification code."}
            {step === 3 && "Personalize your shop profile."}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-xl text-sm font-medium">
            {error}
          </div>
        )}

        {/* STEP 1 FORM */}
        {step === 1 && (
          <form onSubmit={handleSubmitStep1(onRequestOtp)} className="mt-8 space-y-6">
            <div>
              <label htmlFor="recipient" className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                Mobile Number or Email
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 transition-colors group-focus-within:text-blue-600">
                  {recipientValue?.includes('@') ? (
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  {...registerStep1("recipient", {
                    required: "Email or Phone is required",
                    validate: (v: string) => {
                      const value = v.trim();
                      if (value.includes("@")) {
                        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) || "Enter a valid email address";
                      }
                      // Accept 98765 43210, +91 9876543210, 09876543210
                      const digits = value.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "");
                      return /^[6-9]\d{9}$/.test(digits) || "Enter a valid 10-digit mobile number";
                    },
                  })}
                  type="text"
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-lg font-bold"
                  placeholder="e.g. 9876543210"
                />
              </div>
              {errorsStep1.recipient && <p className="mt-2 text-xs text-red-500 font-bold ml-1">{errorsStep1.recipient.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-xl text-lg font-black uppercase tracking-widest text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50 transition-all active:scale-95"
            >
              {isLoading ? "Please wait..." : "Get Started"}
            </button>
          </form>
        )}

        {/* STEP 2 FORM */}
        {step === 2 && (
          <form onSubmit={handleSubmitStep2(onVerifyOtp)} className="mt-6 space-y-8">
            <div className="text-center p-4 bg-blue-50 rounded-2xl border border-blue-100">
               <span className="text-sm font-medium text-blue-800 tracking-tight">Code sent to <br/><strong className="text-lg">{recipient}</strong></span>
               <button type="button" onClick={() => setStep(1)} className="block mx-auto mt-2 text-xs text-blue-600 font-black uppercase tracking-widest hover:underline">Change</button>
            </div>

            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3 text-center">
                6-Digit Verification Code
              </label>
              <input
                {...otpRegister}
                ref={(e) => {
                  otpRef(e);
                  (otpInputRef as any).current = e;
                }}
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="w-full px-4 py-5 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl text-3xl font-black text-center tracking-[0.5em] outline-none transition-all"
                placeholder="000000"
              />
              {errorsStep2.otp && <p className="mt-2 text-xs text-red-500 font-bold text-center">{errorsStep2.otp.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-xl text-lg font-black uppercase tracking-widest text-white bg-blue-600 hover:bg-blue-700 focus:outline-none transition-all active:scale-95"
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>
          </form>
        )}

        {/* STEP 3 FORM */}
        {step === 3 && (
          <form onSubmit={handleSubmitStep3(onCompleteProfile)} className="mt-6 space-y-5">
            <div className="grid grid-cols-1 gap-5">
                 <div>
                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Your Full Name</label>
                    <input
                      {...nameRegister}
                      ref={(e) => {
                        nameRef(e);
                        (nameInputRef as any).current = e;
                      }}
                      className="w-full px-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none font-bold"
                      placeholder="e.g. Rahul Kumar"
                    />
                    {errorsStep3.full_name && <p className="mt-1.5 text-xs text-red-500 font-bold ml-1">{errorsStep3.full_name.message}</p>}
                 </div>
                 <div>
                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Shop Name</label>
                    <input
                      {...registerStep3("shop_name", { required: "Shop Name is required" })}
                      className="w-full px-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none font-bold"
                      placeholder="e.g. Rahul Prints & Xerox"
                    />
                    {errorsStep3.shop_name && <p className="mt-1.5 text-xs text-red-500 font-bold ml-1">{errorsStep3.shop_name.message}</p>}
                 </div>
            </div>

            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Create Secret PIN</label>
              <div className="relative group">
                 <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                 <input
                  {...registerStep3("pin", { 
                      required: "PIN is required",
                      pattern: { value: /^\d+$/, message: "Use digits only" },
                      minLength: { value: 4, message: "Min 4 digits" },
                      maxLength: { value: 6, message: "Max 6 digits" }
                  })}
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-2xl outline-none font-black tracking-[0.5em] placeholder:tracking-normal placeholder:font-medium"
                  placeholder="****"
                 />
              </div>
              {errorsStep3.pin && <p className="mt-1.5 text-xs text-red-500 font-bold ml-1">{errorsStep3.pin.message}</p>}
              <p className="mt-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest ml-1">Use this for quick login next time</p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-5 px-4 border border-transparent rounded-2xl shadow-xl text-xl font-black uppercase tracking-widest text-white bg-green-600 hover:bg-green-700 shadow-green-200 focus:outline-none transition-all active:scale-95 mt-6"
            >
              {isLoading ? "Launching..." : "Open My Shop 🚀"}
            </button>
          </form>
        )}

        <div className="text-center mt-8 pt-6 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="ml-1 text-blue-600 font-black uppercase tracking-wider hover:text-blue-700">
                Sign In
              </Link>
            </p>
        </div>
      </div>
    </div>
  );
};

export default FrictionlessRegistration;
